import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { auditLogApi, clientApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import { formatCurrency } from '@/utils/currency'
import { Activity, ChevronLeft, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

interface AuditLogEntry {
  id: string
  action: string
  entity_type: string
  entity_id: string | null
  user_name: string | null
  client_name: string | null
  changes: Record<string, unknown>
  created_at: string
}

const PAGE_SIZE = 50

const MONETARY_KEYS = new Set([
  'amount', 'cost', 'price', 'unit_price', 'total', 'subtotal',
  'tax', 'balance', 'opening_balance', 'book_value', 'residual_value',
  'accumulated_depreciation',
])

function formatChangeValue(key: string, value: unknown, currency: string): string {
  if (MONETARY_KEYS.has(key) && (typeof value === 'number' || typeof value === 'string')) {
    const n = parseFloat(String(value))
    if (!isNaN(n)) return formatCurrency(n, currency)
  }
  return String(value)
}

const ACTION_BADGE: Record<string, { label: string; variant: 'success' | 'info' | 'destructive' | 'default' }> = {
  create: { label: 'Created', variant: 'success' },
  update: { label: 'Updated', variant: 'info' },
  delete: { label: 'Deleted', variant: 'destructive' },
}

function actionBadge(action: string) {
  const config = ACTION_BADGE[action] ?? { label: action, variant: 'default' as const }
  return <Badge variant={config.variant}>{config.label}</Badge>
}

function formatEntityType(t: string) {
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function ActivityFeed() {
  const { clientId } = useParams<{ clientId?: string }>()
  const { currentFirm } = useAuth()
  const [logs, setLogs] = useState<AuditLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [currency, setCurrency] = useState('USD')

  const isClientFeed = !!clientId
  const title = isClientFeed ? 'Client Activity' : 'Firm Activity'
  const subtitle = isClientFeed ? 'All actions for this client' : 'All actions across the firm'

  useEffect(() => {
    setPage(0)
    if (clientId) clientApi.get(clientId).then((r) => setCurrency(r.data.client.currency)).catch(() => {})
  }, [clientId])

  useEffect(() => { fetchLogs() }, [clientId, page, currentFirm])

  async function fetchLogs() {
    setLoading(true)
    try {
      const params: Record<string, unknown> = { limit: PAGE_SIZE, offset: page * PAGE_SIZE }
      if (clientId) {
        params.client_ledger_id = clientId
      } else if (currentFirm?.id) {
        params.firm_id = currentFirm.id
      }
      const res = await auditLogApi.list(params)
      setLogs(res.data.logs)
      setTotal(res.data.total)
    } finally { setLoading(false) }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">{title}</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">{subtitle}</p>
        </div>
        {total > 0 && (
          <span className="text-[13px] text-[rgba(0,10,30,0.75)]">{total} event{total !== 1 ? 's' : ''}</span>
        )}
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-5 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </CardContent>
        </Card>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Activity className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
            <p className="text-[13px] font-medium text-[#000A1E] mb-1">No activity yet</p>
            <p className="text-[13px] text-[rgba(0,10,30,0.75)]">Actions taken by firm staff will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Time</TableHead>
                    <TableHead>User</TableHead>
                    {!isClientFeed && <TableHead>Client</TableHead>}
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-[rgba(0,10,30,0.75)] whitespace-nowrap text-[13px]">
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-[13px] text-[#000A1E]">{log.user_name || <span className="text-[#CCCED2]">—</span>}</TableCell>
                      {!isClientFeed && (
                        <TableCell className="text-[13px] text-[rgba(0,10,30,0.75)]">{log.client_name || 'Firm'}</TableCell>
                      )}
                      <TableCell>{actionBadge(log.action)}</TableCell>
                      <TableCell className="text-[13px] text-[rgba(0,10,30,0.75)]">
                        {formatEntityType(log.entity_type)}
                      </TableCell>
                      <TableCell className="text-[13px] text-[rgba(0,10,30,0.75)] font-mono max-w-xs truncate">
                        {Object.keys(log.changes).length > 0
                          ? Object.entries(log.changes)
                              .slice(0, 3)
                              .map(([k, v]) => `${k}: ${formatChangeValue(k, v, currency)}`)
                              .join(', ')
                          : <span className="text-[#CCCED2]">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-1">
              <p className="text-[13px] text-[rgba(0,10,30,0.75)]">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost" size="icon-sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-[13px] text-[#000A1E] px-2">{page + 1} / {totalPages}</span>
                <Button
                  variant="ghost" size="icon-sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
